import isNil from 'lodash/isNil'
import {
  InvoiceDeliveryItemDto,
  InvoiceMetadata,
  InvoicePaymentType,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'
import { roundCents } from '../utils'
import { findPairedOe2, resolveTemplatePaymentType } from './invoiceTemplates'

const readAmount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const readDate = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

export const readTotalDue = (
  delivery: Pick<InvoiceDeliveryItemDto, 'invoiceMetadata'>
): number | null =>
  readAmount((delivery.invoiceMetadata as InvoiceMetadata | null)?.totalDue)

/** Where a part 2 value shown for an OE1 comes from, for the drawer's hints. */
export enum OeValueSource {
  TYPED = 'typed',
  OE2 = 'oe2',
  NONE = 'none',
}

interface OeSplit {
  part1Amount: number | null
  part1DueDate: string | null
  part2Amount: number | null
  part2AmountSource: OeValueSource
  part2PaymentType: InvoicePaymentType | null
  /** Typed total, else part 1 + part 2 when both are known. */
  total: number | null
  isTotalTyped: boolean
}

/** Part 1 + part 2, or null until both are known. */
export const addParts = (
  part1: number | null,
  part2: number | null
): number | null =>
  isNil(part1) || isNil(part2) ? null : roundCents(part1 + part2)

/**
 * The Open Enrollment split of an OE1, resolved the way the send does: values
 * typed on the OE1 win, then the uploaded OE2.
 */
export const resolveOeSplit = (
  oe1: InvoiceDeliveryItemDto,
  monthDeliveries: InvoiceDeliveryItemDto[]
): OeSplit => {
  const meta = (oe1.invoiceMetadata as InvoiceMetadata | null) ?? {}
  const oe2 = findPairedOe2(monthDeliveries, oe1)
  const typedAmount = readAmount(meta.balanceDue)
  const oe2Amount = isNil(oe2) ? null : readTotalDue(oe2)

  const part1Amount = readAmount(meta.totalDue)
  const part2Amount = typedAmount ?? oe2Amount
  const typedTotal = readAmount(meta.oeTotalDue)
  return {
    part1Amount,
    part1DueDate: readDate(oe1.dueDate),
    part2Amount,
    part2AmountSource: !isNil(typedAmount)
      ? OeValueSource.TYPED
      : !isNil(oe2Amount)
        ? OeValueSource.OE2
        : OeValueSource.NONE,
    part2PaymentType: resolveTemplatePaymentType(
      InvoiceTemplate.OE_FINAL,
      oe2?.paymentType ?? oe1.profilePaymentType
    ),
    total: typedTotal ?? addParts(part1Amount, part2Amount),
    isTotalTyped: !isNil(typedTotal),
  }
}
